import {
  createBeforeUpload,
  createUploadValidator,
} from '../../../features/file';
/* eslint-disable react-refresh/only-export-components */

import { Input, InputNumber } from '@aigc/antd';
import { AliOssImage } from '@aigc/components';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import type { Pagination } from '@aigc/types';
import { InboxOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  App,
  Button,
  Form,
  Image,
  Skeleton,
  Table,
  Tag,
  Upload,
  type UploadFile,
} from 'antd';
import { RESET } from 'jotai/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { array, mixed, number, object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import fallbackImage from '../../../features/common/fallback.png';
import { Permission } from '../../../features/common/permision';
import { usePreviewFn } from '../../../features/file/hooks';
import type { UploadFileOutputDto } from '../../../features/file/model';
import {
  toFileInputDtoList,
  toFileListFormValues,
} from '../../../features/file/utils';
import { analyzeProductMutationOptions } from '../../../features/product-analysis/api';
import {
  ProductAnalysisDrawer,
  type ProductAnalysisDrawerCloseReason,
} from '../../../features/product-analysis/components/product-analysis-drawer';
import {
  useProductAnalysisCurrentTaskId,
  useProductAnalysisPageData,
} from '../../../features/product-analysis/store';
import { formatProductAnalysisTaskElapsed } from '../../../features/product-analysis/utils';
import { SkillSelect } from '../../../features/skill/components/skill-select';
import {
  getBaseTaskListQueryKey,
  getTaskListQueryOptions,
} from '../../../features/task/api';
import { TaskStatus } from '../../../features/task/components/task-status';
import {
  TaskStatusCode,
  TaskType,
  type ProductAnalysisTaskDto,
} from '../../../features/task/model';

const product_analysis_schema = object({
  product_name: string().required(),
  product_images: array(mixed<UploadFile<UploadFileOutputDto>>().required())
    .min(1, '请上传产品图')
    .required('请上传产品图'),
  region: string().nullable(),
  duration: number().nullable(),
  skill_id: number().meta({ select: true }).nullable().required(),
  extra_prompt: string().trim().nullable(),
});

function ProductAnalysisPage() {
  const { message, notification } = App.useApp();
  const onPreview = usePreviewFn();
  const query_client = useQueryClient();
  const [open_overlay, close_overlay] =
    useOverlay<ProductAnalysisDrawerCloseReason>();
  const [currentTaskId, setCurrentTaskId] = useProductAnalysisCurrentTaskId();
  const active_task_id_ref = useRef<string | undefined>(undefined);
  const [page_data, set_page_data] = useProductAnalysisPageData();
  const [pagination, set_pagination] = useState<Pagination>({
    page: 1,
    size: 10,
  });
  const form = useForm({
    defaultValues: async () => {
      return {
        product_name: page_data.product_name ?? '',
        region: page_data.region,
        duration: page_data.duration,
        skill_id: page_data.skill_id!,
        extra_prompt: page_data.extra_prompt,
        product_images: toFileListFormValues(page_data.product_images ?? []),
      };
    },
    resolver: yupResolver(product_analysis_schema),
  });
  const product_analysis = useMutation(analyzeProductMutationOptions);
  const task_list_query = useQuery({
    ...getTaskListQueryOptions({
      ...pagination,
      task_types: [TaskType.爆款生成],
    }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const tasks = query.state.data?.data;

      if (!tasks) {
        return 5000;
      }

      return tasks.some((task) => !TaskStatus.isSettled(task.status))
        ? 5000
        : false;
    },
    refetchIntervalInBackground: true,
  });

  const openProductAnalysisDrawer = useCallback(
    (task_id: string) => {
      if (active_task_id_ref.current === task_id) {
        return;
      }

      active_task_id_ref.current = task_id;
      setCurrentTaskId(task_id);
      const overlay_ref = open_overlay(
        <ProductAnalysisDrawer
          taskId={task_id}
          onTaskIdChange={(next_task_id) => {
            active_task_id_ref.current = next_task_id;
            setCurrentTaskId(next_task_id);
          }}
        />,
      );

      overlay_ref.afterClosed.then((reason) => {
        if (reason === 'navigate') {
          return;
        }

        active_task_id_ref.current = undefined;

        if (reason === 'manual') {
          setCurrentTaskId(RESET);
        }
      });
    },
    [open_overlay, setCurrentTaskId],
  );

  useEffect(() => {
    if (currentTaskId) {
      openProductAnalysisDrawer(currentTaskId);
      return;
    }

    if (active_task_id_ref.current) {
      active_task_id_ref.current = undefined;
      close_overlay();
    }
  }, [close_overlay, openProductAnalysisDrawer, currentTaskId]);

  useEffect(() => {
    return form.subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        set_page_data({
          product_name: values.product_name,
          product_images: toFileInputDtoList(values.product_images),
          region: values.region,
          duration: values.duration,
          skill_id: values.skill_id,
          extra_prompt: values.extra_prompt,
        });
      },
    });
  }, [form, set_page_data]);

  const accept = useAccept(['.jpg', '.jpeg', '.png', '.webp', '.mp4', '.mp3']);
  useAutoPaste('#uploader', {
    filter: accept.filter,
  });

  const handleStartAnalysis = form.handleSubmit(async (values) => {
    const task = await product_analysis.mutateAsync({
      ...values,
      region: values.region,
      duration: values.duration,
      extra_prompt: values.extra_prompt,
      product_images: toFileInputDtoList(values.product_images),
    });
    set_pagination((current) => ({ ...current, page: 1 }));
    await query_client.invalidateQueries({
      queryKey: getBaseTaskListQueryKey(),
    });
    message.success('爆款生成任务创建成功');
    openProductAnalysisDrawer(task.id);
  });

  return (
    <div className="flex h-full w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:flex-row sm:divide-x sm:divide-y-0">
      <div className="card flex w-full flex-none flex-col sm:overflow-auto xl:w-[480px]">
        <Alert
          type="info"
          showIcon
          title="查看技能包使用手册"
          action={
            <Button
              variant="filled"
              color="blue"
              size="small"
              target="_blank"
              href="https://video-helpdesk.aigpt.com.co/helpdesk/kb-public/articles/qf35gonve4"
            >
              查看
            </Button>
          }
          classNames={{ root: '!mb-4' }}
        ></Alert>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-primary-900">爆款生成</h2>
          <p className="mt-2 text-sm text-primary-500">
            输入商品链接并选择技能，生成商品分析内容
          </p>
        </div>

        <Form
          layout="vertical"
          classNames={{ root: 'grid grid-cols-2 gap-x-6' }}
          onFinish={handleStartAnalysis}
        >
          <FormControl
            className="col-span-2"
            control={form.control}
            name="product_name"
            label="产品名称"
            required
            render={({ field }) => <Input {...field}></Input>}
          ></FormControl>
          <FormControl
            className="col-span-2"
            control={form.control}
            name="product_images"
            label="产品图"
            required
            render={({ field }) => (
              <Upload.Dragger
                accept=".jpg,.jpeg,.png,.webp"
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({
                    accept: '.jpg,.jpeg,.png,.webp',
                  }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                id="uploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture-card"
                multiple
                fileList={field.value}
                onPreview={onPreview}
                onChange={(info) => {
                  const { status } = info.file;

                  if (status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `图片 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(
                    info.fileList.map((item) => {
                      if (item.response) {
                        item.url = item.response.file_url;
                        item.thumbUrl = item.response.file_url;
                      }

                      return item;
                    }),
                  );
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或<span className="text-blue-500">粘贴</span>
                  上传产品图
                </p>
                <p className="ant-upload-hint">支持图片同时批量上传</p>
              </Upload.Dragger>
            )}
          ></FormControl>
          <FormControl
            className="col-span-2"
            control={form.control}
            name="skill_id"
            label="技能"
            required
            render={({ field }) => (
              <SkillSelect
                {...field}
                value={field.value || null}
                className="w-full"
                showSearch={{ optionFilterProp: 'label' }}
              />
            )}
          />
          <FormControl
            control={form.control}
            name="region"
            label="国家/地区"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''}></Input>
            )}
          ></FormControl>
          <FormControl
            control={form.control}
            name="duration"
            label="时长"
            render={({ field }) => (
              <InputNumber
                {...field}
                min={0}
                precision={0}
                suffix="秒"
              ></InputNumber>
            )}
          ></FormControl>
          <FormControl
            className="col-span-2"
            control={form.control}
            name="extra_prompt"
            label="额外要求"
            render={({ field }) => (
              <Input.TextArea
                {...field}
                value={field.value ?? ''}
                autoSize={{ minRows: 4, maxRows: 8 }}
                placeholder="请输入额外的分析要求或提示词（选填）"
              />
            )}
          />
          <Button
            className="col-span-2"
            htmlType="submit"
            variant="solid"
            color="primary"
            size="large"
            loading={product_analysis.isPending}
            block
          >
            🚀 {product_analysis.isPending ? '分析中' : '开始分析'}
          </Button>
        </Form>
      </div>

      <div className="card min-h-[360px] min-w-0 flex-none overflow-y-auto sm:flex-auto">
        <h2 className="mb-4 text-xl font-bold">历史分析</h2>
        <Table<ProductAnalysisTaskDto>
          size="middle"
          tableLayout="fixed"
          loading={task_list_query.isLoading}
          dataSource={task_list_query.data?.data as ProductAnalysisTaskDto[]}
          rowKey={(row) => row.id}
          onRow={(row) => ({
            className: 'cursor-pointer',
            onClick: () => {
              openProductAnalysisDrawer(row.id);
            },
          })}
          columns={[
            {
              title: '产品图',
              width: 96,
              render: (_, task) => {
                const images = (task.generate_data?.product_images ?? []).map(
                  (image) => image.url,
                );

                return (
                  <div onClick={(event) => event.stopPropagation()}>
                    {images.length ? (
                      <Image.PreviewGroup items={images}>
                        <AliOssImage
                          classNames={{ image: 'rounded object-cover' }}
                          size={64}
                          src={images[0]}
                          fallback={fallbackImage}
                          alt={task.generate_data?.product_name || '产品图'}
                        />
                      </Image.PreviewGroup>
                    ) : (
                      <Skeleton.Image classNames={{ content: '!h-16 !w-16' }} />
                    )}
                  </div>
                );
              },
            },
            {
              title: '产品名称',
              width: 200,
              render: (_, task) => (
                <div className="min-w-0 space-y-2">
                  <div className="truncate font-medium">
                    {task.generate_data?.product_name || '-'}
                  </div>
                  <Tag># {task.id}</Tag>
                </div>
              ),
            },
            {
              title: '状态',
              render: (_, task) => (
                <div className="space-y-2">
                  <TaskStatus task={task} />
                  {task.status === TaskStatusCode.任务失败 && (
                    <Alert
                      className="break-words"
                      type="error"
                      title={task.error ?? '爆款生成失败'}
                    />
                  )}
                </div>
              ),
            },
            {
              title: '任务耗时',
              width: 140,
              render: (_, task) => formatProductAnalysisTaskElapsed(task),
            },
            {
              title: '创建时间',
              width: 180,
              render: (_, task) => new Date(task.created_at).toLocaleString(),
            },
          ]}
          pagination={{
            total: task_list_query.data?.total ?? 0,
            current: pagination.page,
            pageSize: pagination.size,
            simple: true,
            hideOnSinglePage: true,
            onChange: (page, size) => {
              set_pagination({ page, size });
            },
          }}
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_creator-center/product/analysis')({
  staticData: { permissions: [Permission.爆款生成] },
  component: ProductAnalysisPage,
});
